/**
 * @author michalstolarski
 * @since 2023-1-27
 */

import * as React from "react";
import styled, { css, useTheme } from "styled-components";

import { AVAILABLE_CURRENCIES } from "@typings/globals";

import { rem } from "@ui/helpers";
import { Cancel } from "@ui/Assets/MyIcons";
import { Info } from "@ui/Assets/Symbolicons";
import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { NormalizedList } from "@ui/Atoms/List";
import { Tooltip } from "@ui/Atoms/Tooltip";

interface ListProps {
  testId?: string;
  className?: string;
  children?: React.ReactNode;
}

const PriceSubpartsList = (props: ListProps): React.ReactElement => (
  <TestWrapper testId={props.testId}>
    <List className={props.className}>{props.children}</List>
  </TestWrapper>
);

type ItemVariant = "default" | "voucher";

interface ItemProps {
  testId?: string;
  className?: string;
  variant?: ItemVariant;
  children: React.ReactNode;
  price: number;
  currency: AVAILABLE_CURRENCIES;
  description?: string | null;
  tooltip?: string | null;
  onVoucherRemove?: () => void;
}

const PriceSubpartsItem = (props: ItemProps): React.ReactElement => {
  const theme = useTheme();

  return (
    <TestWrapper testId={props.testId}>
      <Item className={props.className}>
        <Wrapper>
          <Content>
            {props.variant === "voucher" && (
              <Button type="button" onClick={props.onVoucherRemove}>
                <StyledCancel width={theme.icons.sizes.base_x2} />
              </Button>
            )}

            <Label variant={props.variant}>{props.children}</Label>

            {props.tooltip && (
              <Tooltip placeholder={props.tooltip}>
                <InfoWrapper>
                  <Info
                    variant="solid"
                    size={theme.icons.sizes.base_x3}
                    color={theme.palette.blue500}
                  />
                </InfoWrapper>
              </Tooltip>
            )}
          </Content>

          <PriceWrapper>
            <span>{props.currency}</span>
            <span>{Number(props.price).toFixed(2)}</span>
          </PriceWrapper>
        </Wrapper>

        <Description>{props.description}</Description>
      </Item>
    </TestWrapper>
  );
};

export const PriceSubparts = {
  List: PriceSubpartsList,
  Item: PriceSubpartsItem,
};

const List = styled(NormalizedList)`
  display: grid;
  flex-direction: column;
  gap: ${props => props.theme.margins.base_x1_5};
`;

const Item = styled.li`
  font-size: ${props => props.theme.fonts.sizes.body2};
  line-height: ${props => props.theme.fonts.lineHeights.normal};
  list-style: none;
  overflow: hidden;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${props => props.theme.margins.base_x5};

  ${props => props.theme.breakpoints.desktopToDesktopLarge} {
    gap: ${props => props.theme.margins.base_x3};
  }
`;

const Content = styled.div`
  overflow: hidden;
  display: flex;
  align-items: flex-start;
`;

const Button = styled.button`
  --subpart-button-size: ${props => rem(props.theme.icons.sizes.base_x3)};

  all: unset;
  flex: 0 0 var(--subpart-button-size);
  width: var(--subpart-button-size);
  height: var(--subpart-button-size);
  padding: 0;
  font-size: 0;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

const StyledCancel = styled(Cancel)`
  stroke-width: 2;
`;

const Label = styled.div<{ variant?: ItemVariant }>`
  text-overflow: ellipsis;
  overflow: hidden;

  ${({ variant }) =>
    variant === "voucher" &&
    css`
      font-weight: ${props => props.theme.fonts.weights.semibold};
      color: ${props => props.theme.palette.green700};
      white-space: nowrap;
    `}
`;

const InfoWrapper = styled.div`
  flex: 0 0 auto;
  margin-left: ${props => props.theme.margins.base};
  font-size: 0;
`;

const Description = styled.div`
  font-size: ${props => props.theme.fonts.sizes.overline};
  line-height: ${props => props.theme.fonts.lineHeights.overline};
  color: ${props => props.theme.palette.grey600};
`;

const PriceWrapper = styled.div`
  white-space: nowrap;
  display: grid;
  grid-template-columns: auto auto;
  gap: ${props => props.theme.margins.base};
`;
